/**
 * b→dash ワンタグ連携ユーティリティ
 *
 * 環境変数 VITE_BDASH_SCRIPT_URL が設定されている場合のみタグを読み込む。
 * ワンタグがDOM要素のクリックやページ表示を自動収集する。
 */

import StorageService from '@/utils/StorageService';

declare global {
  interface Window {
    // b→dashリレーション設定で参照するユーザー識別子
    _bdash_user_id?: number;
    // b→dashリレーション設定で参照するバーコードナンバー
    _bdash_barcode_number?: string;
    // b→dashリレーション設定で参照するクーポンID（クーポン操作系ボタンクリック時にセット）
    _bdash_coupon_id?: number;
    // b→dashリレーション設定で参照するクーポン種別（クーポン操作系ボタンクリック時にセット）
    _bdash_coupon_type?: number;
    // b→dashリレーション設定で参照するクーポン名（クーポン操作系ボタンクリック時にセット）
    _bdash_coupon_name?: string;
    // b→dashワンタグのアクセスログ送信関数
    bdash4?: (action: string, params: { type: string }) => void;
  }
}

const ALLOWED_DOMAIN = 'https://cdn.activity.smart-bdash.com/';

let initialized = false;
const isDebug = import.meta.env.VITE_ENV === 'development';

let _readyPromise: Promise<void> = Promise.resolve();

export function setBdashReadyPromise(p: Promise<void>): void {
  _readyPromise = p;
}

export function waitBdashReady(): Promise<void> {
  return _readyPromise;
}

/**
 * b→dash ワンタグスクリプトを読み込む
 * main.ts から1回だけ呼び出す
 */
export function initBdash(): void {
  if (initialized) return;
  initialized = true;

  const scriptUrl = import.meta.env.VITE_BDASH_SCRIPT_URL;
  if (!scriptUrl || !scriptUrl.startsWith(ALLOWED_DOMAIN)) {
    if (scriptUrl) {
      console.warn('[b→dash] 許可されていないドメインのスクリプトURLです');
    }
    return;
  }

  const script = document.createElement('script');
  script.src = scriptUrl;
  script.type = 'text/javascript';
  script.charset = 'UTF-8';
  script.onload = () => isDebug && console.log('[b→dash] スクリプト読み込み完了');
  script.onerror = () => console.error('[b→dash] スクリプトの読み込みに失敗しました');
  document.head.appendChild(script);
}

/**
 * b→dashリレーション設定用にユーザー識別子をwindowにセットする
 * page viewログにもscrambleIDとバーコードナンバーを載せるため、Preferencesにも永続化する
 */
export function setBdashUser(scrambledId: number, barcodeNumber?: string | null): void {
  if (isDebug) {
    console.log('[b→dash] set user', scrambledId, barcodeNumber);
  }
  window._bdash_user_id = scrambledId;
  StorageService.setScrambledId(scrambledId).catch(() => {});
  if (barcodeNumber != null) {
    window._bdash_barcode_number = barcodeNumber;
    StorageService.setBarcodeNumber(barcodeNumber).catch(() => {});
  }
}

/**
 * b→dashリレーション設定用に選択中のクーポン情報をwindowにセットする
 * クーポン操作系ボタン（使う/詳細など）クリック時に呼び出す
 */
export function setBdashCoupon(coupon: { id: number; type: number; name: string }): void {
  if (isDebug) {
    console.log('[b→dash] set coupon', coupon.id, coupon.type, coupon.name);
  }
  window._bdash_coupon_id = coupon.id;
  window._bdash_coupon_type = coupon.type;
  window._bdash_coupon_name = coupon.name;
}

/**
 * SPA遷移時にpage viewを手動送信する
 * router.afterEachから呼び出す（初回ロードはbdashが自動送信するためスキップ）
 */
export function trackPageview(): void {
  if (typeof window.bdash4 === 'function') {
    if (isDebug) {
      console.log('[b→dash] track pageview');
    }
    window.bdash4('send', { type: 'pageview' });
  }
}

/**
 * Preferencesに保存されたscrambled_idとbarcode_numberをwindowへ復元する
 * bdashスクリプト読み込み前に呼び出すことで、page viewログにscrambleIDとバーコードナンバーを載せる
 */
export async function restoreBdashUser(): Promise<void> {
  const [scrambledId, barcodeNumber] = await Promise.all([
    StorageService.getScrambledId(),
    StorageService.getBarcodeNumber(),
  ]);
  if (scrambledId === null) return;
  if (isDebug) {
    console.log('[b→dash] restore user', scrambledId, barcodeNumber ?? '(none)');
  }
  window._bdash_user_id = scrambledId;
  if (barcodeNumber !== null) {
    window._bdash_barcode_number = barcodeNumber;
  }
}
